import React, { useEffect, useState } from "react";
import { Button, Col, Divider, Drawer, Form, message, Row, Space } from "antd";
import {
  CitiesDateTimePicker,
  CitiesInput,
  CitiesRadioGroup,
  CitiesSelect,
  CitiesTextEditor,
} from "../../components";
import { services as api } from "../../services";
import {
  clientPriorityOps,
  followUpStatus,
  onlyDate,
  utcDateTimeToLocalPAOnly,
} from "../../shared";
import dayjs from "dayjs";
import { draftapi } from "../../services/draftapi";
import { DraftNoteTable } from "./DraftNoteTable";
import { CallBackHistory } from "./CallBackHistory";

const AddEditDraft = ({ visible, editItem, onClose, call }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [serviceLookups, setServicesLookup] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [clientLookups, setClientLookup] = useState([]);
  const [selectedClient, setSelectedClient] = useState([]);
  const [draftNotes, setDraftNotes] = useState(null);

  useEffect(() => {
    if (visible && editItem) {
      form.setFieldsValue({
        ...editItem,
        when_to_call_again: utcDateTimeToLocalPAOnly(
          editItem?.when_to_call_again,
        ),
        any_notes: null,
      });
      GetAllNotes();
    } else form.resetFields();
  }, [visible, editItem, form]);

  const AddEditMedicines = async () => {
    const data = form.getFieldsValue();
    const payload = {
      clientID: data?.clientID,
      serviceIDs: data?.serviceIDs,
      client_wants_treatment: data?.client_wants_treatment,
      first_visit_or_returning_client: data?.first_visit_or_returning_client,
      when_to_call_again: dayjs(data?.when_to_call_again).format(
        "YYYY-MM-DD HH:mm",
      ),
      budget_range: data?.budget_range,
      client_priority: data?.client_priority,
      follow_up_status: data?.follow_up_status,
      any_notes: data?.any_notes,
    };
    setLoading(true);
    const result = await api?.draftapi?.addEditDraft(payload, editItem?.id);

    if (result?.success) {
      message.success(
        `Draft ${editItem?.id ? "updated" : "saved"} successfully!`,
      );
      onClose();
      call();
    } else {
      message.error(
        result?.message ||
          `Failed to ${editItem?.id ? "update" : "save"} Draft`,
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    if (api) {
      getServicesLookUp();
      getClientsLookUp();
    }
  }, [api]);
  const getServicesLookUp = async () => {
    const result = await api?.servicesService?.getServicesLookUp();
    setServicesLookup(result);
  };
  const getClientsLookUp = async () => {
    const result = await api?.clientapi?.getClientLookups();
    setClientLookup(result);
  };

  const GetAllNotes = async () => {
    try {
      const result = await draftapi?.getAllDrafsNotes(editItem?.id);
      if (result) {
        setDraftNotes(result?.data);
      }
    } catch (error) {
      console.log(error);
      message.error(error || "Failed to load draft notes");
    }
  };
  return (
    <>
      <Drawer
        title={(editItem ? "Update" : "Add") + " Draft"}
        onClose={onClose}
        open={visible}
        size="large"
        footer={
          <div className="justify-end">
            <Space>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="primary"
                loading={loading}
                onClick={() => form.submit()}
              >
                {(editItem ? "Update" : "Add") + " Draft"}
              </Button>
            </Space>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={true}
          onFinish={AddEditMedicines}
          requiredMark={false}
        >
          <Row gutter={16}>
            <Col span={24} md={12}>
              <CitiesSelect
                name="clientID"
                label="Client"
                placeholder="Choose client"
                required
                message="Please enter client"
                options={clientLookups}
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                showSearch
                allowClear
              />
            </Col>
            <Col span={24} md={12}>
              <CitiesSelect
                mode={"multiple"}
                name="serviceIDs"
                label="Services"
                placeholder="Choose services"
                required
                message="Please enter services"
                options={serviceLookups}
                // filterOption={(input, option) =>
                //   option?.children?.toLowerCase().includes(input.toLowerCase())
                // }
                filterOption={(input, option) => {
                  const labelText = String(
                    option?.children ?? "",
                  ).toLowerCase();
                  const cleanedInput = input
                    .toLowerCase()
                    .replace(/\s+/g, " ")
                    .trimEnd();

                  return labelText.includes(cleanedInput);
                }}
                allowClear
              />
            </Col>
            <Col span={24} md={12}>
              <CitiesInput
                name="client_wants_treatment"
                label="Client Treatment"
                placeholder="Enter treatment"
                required
                message={"Please enter treatment"}
              />
            </Col>
            <Col span={24} md={12}>
              <CitiesDateTimePicker
                datePicker
                name={"when_to_call_again"}
                label="Call Again"
                placeholder="Select date"
                required
                message={"Please select date"}
                disabledDate={(current) =>
                  current && current.isBefore(dayjs(), "day")
                }
              />
            </Col>
            <Col span={24}>
              <CitiesRadioGroup
                name="first_visit_or_returning_client"
                label="First Visit"
                required
                message={"Please choose visit"}
                options={[
                  { name: "Yes", value: true },
                  { name: "No", value: false },
                ]}
              />
            </Col>
            <Col span={24}>
              <CitiesInput
                name="budget_range"
                label="Budget"
                placeholder="Enter budget"
                required
                message={"Please enter budget"}
              />
            </Col>
            <Col span={24} md={12}>
              <CitiesSelect
                name="client_priority"
                label="Client Priority"
                placeholder="Choose client priority"
                required
                message="Please enter client priority"
                options={clientPriorityOps}
              />
            </Col>
            <Col span={24} md={12}>
              <CitiesSelect
                name="follow_up_status"
                label="Follow Up Status"
                placeholder="Choose follow up status"
                required
                message="Please enter follow up status"
                options={followUpStatus}
              />
            </Col>
            <Col span={24}>
              <CitiesTextEditor
                name="any_notes"
                label="Note"
                placeholder="Write a note..."
              />
            </Col>
            {editItem && (
              <>
                <Col span={24}>
                  <DraftNoteTable
                    editDraft={editItem}
                    notesData={draftNotes}
                    call={() => GetAllNotes()}
                  />
                </Col>
                <Divider className="mt-3" />
                <Col span={24}>
                  <CallBackHistory editDraft={editItem} />
                </Col>
              </>
            )}
          </Row>
        </Form>
      </Drawer>
    </>
  );
};
export { AddEditDraft };
